import React, { useState } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  IconButton,
  Tooltip
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import MainCard from 'components/MainCard';
import { EyeFilled } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFeatureNotification } from 'services/feature-notification/createFeatureNotification';
import { getFeatureNotification } from 'services/feature-notification/getFeatureNotification';
import { editFeatureNotification } from 'services/feature-notification/editFeatureNotification';
import { deleteFeatureNotification } from 'services/feature-notification/deleteFeatureNotification';
import toast from 'react-hot-toast';

const roleOptions = ['Admin', 'Student', 'Coach'];

export default function FeatureAnnouncement() {
  const [openDialog, setOpenDialog] = useState(false);
  const [newAnn, setNewAnn] = useState({ title: '', message: '', roles: [], link: '' });
  const [editId, setEditId] = useState(null);

  const { data, refetch } = useQuery({
    queryFn: getFeatureNotification,
    queryKey: ['notifications']
  });

  const featureNotificationsData = data?.data;

  const createMutation = useMutation({
    mutationFn: createFeatureNotification,
    onSuccess: () => {
      toast.success('Notification created successfully');
      refetch();
      resetDialog();
    },
    onError: () => toast.error('Failed to create notification')
  });

  const updateMutation = useMutation({
    mutationFn: editFeatureNotification,
    onSuccess: () => {
      toast.success('Notification updated successfully');
      refetch();
      resetDialog();
    },
    onError: () => toast.error('Failed to update notification')
  });

  const deleteMutation = useMutation({
    mutationFn: deleteFeatureNotification,
    onSuccess: () => {
      toast.success('Notification deleted');
      refetch();
    },
    onError: () => toast.error('Failed to delete notification')
  });

  const resetDialog = () => {
    setOpenDialog(false);
    setNewAnn({ title: '', message: '', roles: [], link: '' });
    setEditId(null);
  };

  const handleCreateOrUpdate = () => {
    if (!newAnn.title || !newAnn.message || newAnn.roles.length === 0) {
      toast.error('All fields are required');
      return;
    }

    const roleMap = { Admin: 1, Student: 2, Coach: 3 };
    const payload = {
      title: newAnn.title,
      message: newAnn.message,
      link: newAnn.link,
      roleIds: newAnn.roles.map((r) => roleMap[r])
    };

    if (editId) {
      updateMutation.mutate({ notificationid: editId, ...payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleEdit = (item) => {
    const roleMapReverse = { 1: 'Admin', 2: 'Student', 3: 'Coach' };
    setNewAnn({
      title: item.title,
      message: item.message,
      roles: item.roleIds.map((id) => roleMapReverse[id]),
      link: item.link
    });
    setEditId(item.notificationid);
    setOpenDialog(true);
  };

  const handleDelete = (id) => {
    const confirmDelete = window.confirm('Are you sure you want to delete this notification?');
    if (confirmDelete) {
      deleteMutation.mutate(id);
    }
  };

  return (
    <MainCard>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Feature Announcements</Typography>
        <Button
          variant="contained"
          onClick={() => {
            setNewAnn({ title: '', message: '', roles: [], link: '' });
            setEditId(null);
            setOpenDialog(true);
          }}
        >
          Create Notification
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Message</TableCell>
              <TableCell>Roles</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Feature Link</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {featureNotificationsData?.length ? (
              featureNotificationsData.map((item, index) => (
                <TableRow key={item.notificationid}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{item.title}</TableCell>
                  <TableCell>{item.message}</TableCell>
                  <TableCell>
                    {item.roleIds.length > 1 ? (
                      <Tooltip title={item.roleIds.map((id) => roleOptions[id - 1]).join(', ')} arrow>
                        <Chip label={`${roleOptions[item.roleIds[0] - 1]} +${item.roleIds.length - 1}`} size="small" />
                      </Tooltip>
                    ) : (
                      <Chip label={roleOptions[item.roleIds[0] - 1]} size="small" />
                    )}
                  </TableCell>

                  <TableCell>{new Date(item.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    {item.link ? (
                      <Tooltip title="View Feature">
                        <IconButton component="a" href={item.link} target="_blank" rel="noopener noreferrer" color="secondary">
                          <EyeFilled />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      'N/A'
                    )}
                  </TableCell>
                  <TableCell>
                    <IconButton color="primary" onClick={() => handleEdit(item)}>
                      <Edit />
                    </IconButton>
                    <IconButton color="error" onClick={() => handleDelete(item.notificationid)}>
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No feature announcements found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editId ? 'Edit Announcement' : 'Create Feature Announcement'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField label="Title" fullWidth value={newAnn.title} onChange={(e) => setNewAnn({ ...newAnn, title: e.target.value })} />
            <TextField
              label="Message"
              fullWidth
              multiline
              rows={4}
              value={newAnn.message}
              onChange={(e) => setNewAnn({ ...newAnn, message: e.target.value })}
            />
            <TextField
              label="Feature Link (optional)"
              fullWidth
              value={newAnn.link}
              onChange={(e) => setNewAnn({ ...newAnn, link: e.target.value })}
            />
            <FormControl fullWidth>
              <InputLabel id="roles-label">Select Roles</InputLabel>
              <Select
                labelId="roles-label"
                multiple
                value={newAnn.roles}
                onChange={(e) => setNewAnn({ ...newAnn, roles: e.target.value })}
                input={<OutlinedInput label="Select Roles" />}
                renderValue={(selected) => selected.join(', ')}
              >
                {roleOptions.map((role) => (
                  <MenuItem key={role} value={role}>
                    <Checkbox checked={newAnn.roles.indexOf(role) > -1} />
                    <ListItemText primary={role} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateOrUpdate}>
            {editId ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </MainCard>
  );
}
